import { Injectable } from "@angular/core";
import { async } from "@angular/core/testing";
import { Observable } from "rxjs";
import { resolve } from "url";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { ContentType, OrderDataAddressFieldInputs } from "../../shared/contentTypes";
import { CustomerInputFieldViewModel, WebshopSettingsDataModel2, WebshopSettingsDMKey } from "../../shared/models";
import { AddressType, FurtherAddress, RemoveCustomerFurtherAddressSuccessModel, UserAddressViewModel } from "../../shared/models/userInfo.model";
import Utils from "../../shared/utils";

@Injectable()
export class UserAddressService {

  private _webshopSettingsObsServ: IObjectObserver<WebshopSettingsDataModel2>;
  public get webshopSettingsDataModelObserveChanges(): Observable<(string | number)[]> {
    return this._webshopSettingsObsServ.ObserveChanges;
  }

  constructor(private _circularInjectionHelper: CircularInjectionHelper) {
    this._webshopSettingsObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebshopSettingsDataModel2>(ContentType.WebshopSettings, _circularInjectionHelper);
  }

  public getAddressInputFields(): Promise<CustomerInputFieldViewModel[]> {
    const me = this;
    return new Promise<CustomerInputFieldViewModel[]>((resolve, reject) => {
      me._webshopSettingsObsServ.GetObject(WebshopSettingsDMKey)
        .then((webshopSettingsDataModel) => {
          const jasonInputs = JSON.parse(webshopSettingsDataModel.customer_input_fields) as Object[];
          const addressInputs = jasonInputs.filter(a => Object.keys(OrderDataAddressFieldInputs).includes(a["field"]));
          resolve(me.createAddressInputFields(addressInputs));
        })
        .catch(error => {
          this._circularInjectionHelper.coreLogService.error("UserAddressService.getAddressInputFields", "._webshopSettingsObsServ.GetObject", error);
          reject(error);
        });
    })
  }

  private createAddressInputFields(jasonInputs: Object[]): CustomerInputFieldViewModel[] {
    const me = this;
    const addressInputFieldViewModels = new Array<CustomerInputFieldViewModel>();
    const jsonInputs = jasonInputs.filter(j => (j["usedDelivery"]));
    jsonInputs.forEach(jsonObject => {
      const addressInputFielsViewModel = new CustomerInputFieldViewModel();
      addressInputFielsViewModel.fieldName = Utils.EnumName(OrderDataAddressFieldInputs, +(jsonObject["field"]));
      addressInputFielsViewModel.isRequired = jsonObject["requiredDelivery"];
      addressInputFielsViewModel.fieldType = jsonObject["type"];
      addressInputFieldViewModels.push(addressInputFielsViewModel);
    });
    return addressInputFieldViewModels;
  }

  /**get user delivery address by token
   * @param token used to get the delivery address by token
   */
  public GetUserDeliveryAddressByToken(token: string): Promise<UserAddressViewModel> {
    const me = this;
    return new Promise<UserAddressViewModel>((resolve, reject) => {
      me._circularInjectionHelper.yourAppServer.User.GetUserInfo(true)
        .then((userInfo) => {
          if (userInfo.further_addresses.some(a => a.token === token)) {
            const deliveryAddress = userInfo.further_addresses.find(a => a.token === token);
            resolve(me.mapUserInfoToDeliveryAddressViewModel(deliveryAddress));
          } else {
            resolve(null);
            this._circularInjectionHelper.coreLogService.error("UserAddressService.GetUserDeliveryAddressById", "_circularInjectionHelper.yourAppServer.User.GetUserInfo()", "no delivery address Found");
          }
        })
        .catch((error) => {
          reject();
          this._circularInjectionHelper.coreLogService.error("UserAddressService.GetUserDeliveryAddressById", "_circularInjectionHelper.yourAppServer.User.GetUserInfo()", error);
        });
    });
  }

  public RemoveDeliveryAddress(token: string): Promise<void> {
    const me = this;
    return new Promise<void>(async (resolve, reject) => {
      try {
        await me._circularInjectionHelper.yourAppServer.User.RemoveCustomerFurtherAddressDataModel(token);
        await me._circularInjectionHelper.yourAppServer.User.GetUserInfo(true);
        resolve();
      } catch (error) {
        this._circularInjectionHelper.coreLogService.error("UserAddressService.RemoveDeliveryAddress", "try catch", error);
        reject();
      }
    })
  }

  public saveCustomerDeliveryAddressDataModel(userAddressViewModel: UserAddressViewModel): Promise<void> {
    const me = this;
    return new Promise<void>(async (resolve, reject) => {
      try {

        const deliveryAddress: { [key: string]: string | number } = {};
        deliveryAddress["AddresstypeID"] = AddressType.Delivery;
        for (const key in userAddressViewModel) {
          if (Object.prototype.hasOwnProperty.call(userAddressViewModel, key)) {
            switch (key) {
              case Utils.EnumName(OrderDataAddressFieldInputs, OrderDataAddressFieldInputs.addition):
                {
                  deliveryAddress["Addition"] = userAddressViewModel.addition ? userAddressViewModel.addition.trim() : "";
                  break;
                }
              case Utils.EnumName(OrderDataAddressFieldInputs, OrderDataAddressFieldInputs.address):
                {
                  deliveryAddress["Adress"] = userAddressViewModel.address ? userAddressViewModel.address.trim() : "";
                  break;
                }
              case Utils.EnumName(OrderDataAddressFieldInputs, OrderDataAddressFieldInputs.city):
                {
                  deliveryAddress["City"] = userAddressViewModel.city ? userAddressViewModel.city.trim() : "";
                  break;
                }
              case Utils.EnumName(OrderDataAddressFieldInputs, OrderDataAddressFieldInputs.zip_code):
                {
                  deliveryAddress["Plz"] = userAddressViewModel.zip_code ? userAddressViewModel.zip_code.trim() : "";
                  break;
                }
              case Utils.EnumName(OrderDataAddressFieldInputs, OrderDataAddressFieldInputs.country):
                {
                  deliveryAddress["Country"] = userAddressViewModel.country ? userAddressViewModel.country.trim() : "";
                  break;
                }
              case Utils.EnumName(OrderDataAddressFieldInputs, OrderDataAddressFieldInputs.co):
                {
                  deliveryAddress["CusCo"] = userAddressViewModel.co ? userAddressViewModel.co.trim() : "";
                  break;
                }

              default:
                break;
            }
          }
        }
        await me._circularInjectionHelper.yourAppServer.User.SetCustomerFurtherAddressDataModel(userAddressViewModel.id ? userAddressViewModel.id : "", deliveryAddress);
        await me._circularInjectionHelper.yourAppServer.User.GetUserInfo(true);
        resolve();
      } catch (error) {
        this._circularInjectionHelper.coreLogService.error("UserAddressService.saveCustomerDeliveryAddressDataModel", "try catch", error);
        reject();
      }
    });
  }

  private mapUserInfoToDeliveryAddressViewModel(model: FurtherAddress): UserAddressViewModel {
    const me = this;
    const viewModel = new UserAddressViewModel()
    viewModel.id = model.token;
    viewModel.co = model.co;
    viewModel.address = model.address;
    viewModel.addition = model.addition;
    viewModel.zip_code = model.zip;
    viewModel.city = model.city;
    viewModel.country = model.country;
    return viewModel;
  }

}
